const BASE_URL = 'https://statsapi.mlb.com/api';

/**
 * Common fetch wrapper for the MLB Stats API.
 * Handles base URL, parsing JSON, and verifying response status.
 */
export const apiSync = async (endpoint, options = {}) => {
  const url = `${BASE_URL}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
  
  const defaultOptions = {
    method: 'GET',
    credentials: 'omit',
    headers: {
      'Accept': 'application/json',
    },
    ...options,
  };

  // Only add Content-Type: application/json for POST/PUT/PATCH to avoid CORS preflight on GET
  if (['POST', 'PUT', 'PATCH'].includes(defaultOptions.method)) {
    defaultOptions.headers = {
      ...defaultOptions.headers,
      'Content-Type': 'application/json',
    };
  }

  try {
    const response = await fetch(url, defaultOptions);
    
    // fetch does NOT throw for non-2xx status codes by default
    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown Error');
      throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    // Response validation - ensuring we only parse what we expect
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      throw new Error(`Unexpected content-type from MLB API: ${contentType || 'missing'}`);
    }

    // Attempt to parse JSON; throw if parsing fails to avoid downstream crashes
    try {
      return await response.json();
    } catch (parseError) {
      throw new Error(`Failed to parse MLB API response as JSON: ${parseError.message}`);
    }
  } catch (error) {
    console.error(`Fetch error at ${url}:`, error);
    throw error;
  }
};

const api = {
  get: (endpoint, options) => apiSync(endpoint, { ...options, method: 'GET' }),
  post: (endpoint, body, options) => apiSync(endpoint, { ...options, method: 'POST', body: JSON.stringify(body) }),
  put: (endpoint, body, options) => apiSync(endpoint, { ...options, method: 'PUT', body: JSON.stringify(body) }),
  delete: (endpoint, options) => apiSync(endpoint, { ...options, method: 'DELETE' }),
};

export default api;
